/* eslint-disable react/prop-types */
import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Switch } from '@/components/ui/switch';
import { Label } from '@/components/ui/label';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  MapPin,
  Settings,
  Facebook,
  Instagram,
  Twitter,
  Youtube,
  Linkedin,
  Download,
  ChevronRight,
  MessageCircle,
  Share2,
  Zap,
  LogOut,
  User,
  Grid,
  Image,
  BriefcaseBusiness,
  RefreshCcwDot,
  IndianRupee,
  Tag,
  Book,
  Truck,
  HelpCircle,
  Scale,
} from 'lucide-react';
import { useAuth } from '@/contexts/AuthContext';
import { useUser } from '@/contexts/UserContext';
import { vendorApi } from '@/pages/vendor/api/api';
import { usePWAInstall } from '@/hooks/usePwa';

const VendorProfile = () => {
  // --- STATE AND HOOKS ---
  const [loading, setLoading] = useState(false);
  const [isIosModalOpen, setIsIosModalOpen] = useState(false);
  const [isSocialModalOpen, setIsSocialModalOpen] = useState(false);
  const { user, refreshUser } = useUser();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const { canInstall, isInstalling, installApp, showFallbackInstructions } = usePWAInstall();
  const isOnline = user?.status === 'Approved';

  // --- FUNCTIONS ---
  const fetchData = async () => {
    const phone = localStorage.getItem('contactNo');
    if (!phone) {
      toast.error('Could not find user information.');
      return;
    }
    await refreshUser(phone, true);
  };

  const handleLogout = () => logout();

  const handleStatusToggle = async () => {
    if (!user?.id || (user.status !== 'Approved' && user.status !== 'OutOfStation')) return;
    const newStatus = isOnline ? 'OutOfStation' : 'Approved';
    setLoading(true);
    try {
      const result = await vendorApi.updateVendorStatus(user.id, newStatus);
      if (result.success) {
        toast.success(`Your shop is now ${newStatus === 'Approved' ? 'Live' : 'Offline'}.`);
        await fetchData();
      } else {
        toast.error('Failed to update status.');
      }
    } catch (error) {
      console.error(error);
      toast.error('An error occurred while updating status.');
    } finally {
      setLoading(false);
    }
  };

  const handleInstallPwa = async () => {
    const result = await installApp(() => setIsIosModalOpen(true));
    if (result.success && result.message !== 'iOS install instructions shown') {
      toast.success(result.message);
    } else if (result.message === 'No install prompt available') {
      const instructions = showFallbackInstructions();
      toast.info(`To install the app: ${instructions}`, { autoClose: 8000 });
    }
  };

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  // --- MENU ITEM DEFINITIONS ---
  const profileMenuItems = [
    {
      icon: User,
      title: 'My Profile',
      subtitle: 'Manage your personal information',
      onClick: () => navigate('/profile/edit'),
    },
    {
      icon: BriefcaseBusiness,
      title: 'About Business',
      subtitle: 'Tell your customer more about your Business',
      onClick: () => navigate('/profile/business/edit'),
    },
    {
      icon: MapPin,
      title: 'Manage Address',
      subtitle: 'Add and edit your business address',
      onClick: () => navigate('/address', { state: { address: user?.address } }),
    },
    {
      icon: Settings,
      title: 'Order Settings',
      subtitle: 'Configure orders, delivery, and categories',
      onClick: () => navigate('/profile/settings'),
    },
    {
      icon: Grid,
      title: 'Categories',
      subtitle: 'Your Categories',
      onClick: () => navigate('/profile/categories/edit'),
    },
    {
      icon: Image,
      title: 'Media',
      subtitle: 'Your Gallery',
      onClick: () => navigate('/profile/media/edit'),
    },
  ];

  const supportMenuItems = [
    {
      icon: Share2,
      title: 'Social Media',
      subtitle: 'Connect and share on social platform',
      onClick: () => navigate('/profile/social'),
    },
    {
      icon: MessageCircle,
      title: 'Contact Support',
      subtitle: 'Send us a message or feedback',
      onClick: () => navigate('/profile/contact'),
    },
    {
      icon: HelpCircle,
      title: 'FAQ',
      subtitle: 'Frequently Asked Questions',
      onClick: () => navigate('/faq'),
    },
    {
      icon: Tag,
      title: 'Terms & Conditions',
      subtitle: 'Terms & Conditions details',
      onClick: () => navigate('/terms'),
    },
    {
      icon: Book,
      title: 'Privacy Policy',
      subtitle: 'Privacy Policy details',
      onClick: () => navigate('/privacy'),
    },
    {
      icon: Scale,
      title: 'Legal Disclaimer',
      subtitle: 'Legal Disclaimer details',
      onClick: () => navigate('/legal-disclaimer'),
    },
    {
      icon: RefreshCcwDot,
      title: 'Return policy',
      subtitle: 'Return Policy details',
      onClick: () => navigate('/return-policy'),
    },
    {
      icon: IndianRupee,
      title: 'Refund & Cancellation',
      subtitle: 'Refund/Cancellation Policy details',
      onClick: () => navigate('/refund-policy'),
    },
    {
      icon: Truck,
      title: 'Shipping Policy',
      subtitle: 'Shipping Policy Details',
      onClick: () => navigate('/shipping-policy'),
    },
  ];

  return (
    <div className="h-full overflow-y-auto bg-slate-50">
      {/* Responsive Main Content Grid */}
      <div className="p-4 md:p-6 lg:grid lg:grid-cols-5 lg:gap-6 lg:items-start">
        {/* Left Column (Primary Info) - Sticky on desktop */}
        <div className="lg:col-span-2 lg:sticky lg:top-4 lg:h-fit">
          <div className="flex flex-col gap-y-6">
            {/* User Hero Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 p-6">
              <div className="flex flex-col items-center text-center">
                <div className="relative">
                  <div className="relative flex items-center justify-center size-24 bg-slate-200 rounded-full">
                    {user?.profileImage ? (
                      <Avatar className="size-24">
                        <AvatarImage src={user.profileImage} alt={user.name || 'User Profile'} />
                        <AvatarFallback>
                          {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                        </AvatarFallback>
                      </Avatar>
                    ) : (
                      <User className="size-16" />
                    )}
                  </div>
                  {/* Animated Gradient Ring */}
                  <div className="absolute -inset-1 rounded-full border-2 border-transparent bg-gradient-to-r from-blue-500 via-purple-500 to-pink-500 bg-clip-border animate-[spin_6s_linear_infinite] -z-10" />
                </div>
                <h2 className="mt-4 text-lg md:text-xl font-bold tracking-tight text-slate-900">
                  {user?.shopName}
                </h2>
                <p className="mt-1 text-sm text-slate-600">
                  Operated by{' '}
                  <span className="font-semibold bg-gradient-to-r from-blue-600 to-indigo-600 bg-clip-text text-transparent">
                    {user?.name}
                  </span>
                </p>
              </div>
            </div>

            {/* Online Status Toggle Section */}
            <div className="bg-white rounded-xl shadow-sm border border-slate-200/80">
              <div className="flex p-4 pb-3 items-center justify-between border-b border-slate-100">
                <div className="flex items-center gap-3">
                  <div className={`p-2 rounded-lg ${isOnline ? 'bg-green-100' : 'bg-yellow-100'}`}>
                    <Zap className={`h-5 w-5 ${isOnline ? 'text-green-600' : 'text-yellow-600'}`} />
                  </div>
                  <div>
                    <Label htmlFor="status-toggle" className="text-sm font-semibold text-slate-800">
                      Online Status
                    </Label>
                    <p className="text-xs text-slate-500">
                      {isOnline ? 'Live & accepting orders' : 'Currently offline'}
                    </p>
                  </div>
                </div>
                <Switch
                  id="status-toggle"
                  checked={isOnline}
                  onCheckedChange={handleStatusToggle}
                  disabled={
                    loading || (user?.status !== 'Approved' && user?.status !== 'OutOfStation')
                  }
                  aria-label="Toggle shop status"
                />
              </div>
              {/* install button */}
              <button
                onClick={handleInstallPwa}
                disabled={isInstalling}
                className="w-full p-4 flex items-center gap-3 text-left group transition-colors hover:bg-slate-50"
              >
                <div className="p-2 rounded-lg bg-orange-100 transition-colors">
                  <Download className="h-5 w-5 text-orange-600 transition-colors" />
                </div>
                <div className="flex-1">
                  <h4 className="font-semibold text-sm text-slate-800">
                    {isInstalling ? 'Installing...' : 'Install App'}
                  </h4>
                  <p className="text-xs text-slate-500">Get a faster experience</p>
                </div>
                <ChevronRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            {/* Logout Button - Desktop only */}
            <Button
              onClick={handleLogout}
              variant="ghost"
              className="w-full hidden lg:flex text-red-500 hover:bg-red-50 hover:text-red-600 font-semibold text-sm py-5 group"
            >
              <LogOut className="mr-2 h-5 w-5 transition-transform group-hover:scale-110" />
              Log Out
            </Button>
          </div>
        </div>

        {/* Right Column (Menus) - Scrollable */}
        <div className="lg:col-span-3 pb-20 lg:pb-4 flex flex-col gap-y-6 mt-6 lg:mt-0">
          {/* Reusable Menu Section Component */}
          <MenuSection
            title="Account Settings"
            items={profileMenuItems.filter((item) => item.show !== false)}
            isInstalling={isInstalling}
          />
          <MenuSection title="Help & Support" items={supportMenuItems} />

          {/* Logout Button - Mobile/Tablet */}
          <Button
            onClick={handleLogout}
            variant="ghost"
            className="w-full lg:hidden text-red-500 hover:bg-red-50 hover:text-red-600 font-semibold text-sm py-5 group"
          >
            <LogOut className="mr-2 h-5 w-5 transition-transform group-hover:scale-110" />
            Log Out
          </Button>
        </div>
      </div>

      {/* Modals */}
      <SocialsDialog isOpen={isSocialModalOpen} onOpenChange={setIsSocialModalOpen} />
      <Dialog open={isIosModalOpen} onOpenChange={setIsIosModalOpen}>
        <DialogContent className="sm:max-w-md w-11/12 max-w-sm rounded-lg p-0 overflow-hidden">
          <img src="/iosdemo.webp" alt="iOS installation tutorial" className="h-auto w-full" />
        </DialogContent>
      </Dialog>
    </div>
  );
};

// --- SUB-COMPONENTS for cleaner code ---

const MenuSection = ({ title, items, isInstalling = false }) => (
  <div className="space-y-3">
    <h3 className="text-sm font-semibold text-slate-500 uppercase tracking-wider px-2">{title}</h3>
    <div className="bg-white rounded-xl shadow-sm border border-slate-200/80 divide-y divide-slate-100">
      {items.map((item) => (
        <MenuItem key={item.title} item={item} isInstalling={isInstalling} />
      ))}
    </div>
  </div>
);

const MenuItem = ({ item, isInstalling }) => {
  const { icon: Icon, title, subtitle, onClick } = item;
  const isInstallButton = title === 'Install App';

  return (
    <button
      onClick={onClick}
      disabled={isInstallButton && isInstalling}
      className="w-full p-4 flex items-center gap-4 text-left group transition-colors hover:bg-slate-50"
    >
      <div className="p-2.5 bg-slate-100 rounded-lg group-hover:bg-blue-50 transition-colors">
        <Icon className="h-5 w-5 text-slate-600 group-hover:text-blue-600 transition-colors" />
      </div>
      <div className="flex-1">
        <h4 className="font-semibold text-slate-800">
          {isInstallButton && isInstalling ? 'Installing...' : title}
        </h4>
        <p className="text-sm text-slate-500">{subtitle}</p>
      </div>
      <ChevronRight className="h-5 w-5 text-slate-400 transition-transform group-hover:translate-x-1" />
    </button>
  );
};

const SocialsDialog = ({ isOpen, onOpenChange }) => {
  const socialLinks = [
    { name: 'Twitter / X', href: 'https://x.com/biglocal_in', icon: Twitter },
    {
      name: 'Facebook',
      href: 'https://www.facebook.com/profile.php?id=61572946807977',
      icon: Facebook,
    },
    { name: 'Instagram', href: 'https://www.instagram.com/biglocal_in', icon: Instagram },
    { name: 'LinkedIn', href: 'https://www.linkedin.com/company/biglocal-in/', icon: Linkedin },
    { name: 'YouTube', href: '#', icon: Youtube },
  ];

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md rounded-xl">
        <DialogHeader>
          <DialogTitle className="text-xl font-bold text-center">Follow Us</DialogTitle>
        </DialogHeader>
        <div className="grid grid-cols-1 gap-3 pt-4">
          {socialLinks.map(({ name, href, icon: Icon }) => (
            <a
              key={name}
              href={href}
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center gap-4 p-3 rounded-lg hover:bg-slate-100 transition-colors"
            >
              <Icon className="h-6 w-6 text-slate-600" />
              <span className="font-medium text-slate-700">{name}</span>
              <ChevronRight className="h-5 w-5 text-slate-400 ml-auto" />
            </a>
          ))}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default VendorProfile;
